import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Dumbbell, Calendar, Users, CreditCard } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../contexts/AuthContext';

const Landing = () => {
  const { user } = useAuth();

  const features = [
    {
      icon: <Dumbbell className="w-8 h-8" />,
      title: 'Diverse Classes',
      description: 'From HIIT to Yoga, find the perfect workout for your goals'
    },
    {
      icon: <Calendar className="w-8 h-8" />,
      title: 'Easy Booking',
      description: 'Reserve your spot in seconds with our streamlined system'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Expert Instructors',
      description: 'Train with certified professionals who push you to excel'
    },
    {
      icon: <CreditCard className="w-8 h-8" />,
      title: 'Secure Payments',
      description: 'Safe and easy checkout with PayPal integration'
    }
  ];

  return (
    <div className="min-h-screen bg-background">
      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 bg-background/80 backdrop-blur-md border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <Dumbbell className="w-8 h-8 text-primary" />
            <span className="font-heading text-2xl font-bold tracking-tight">T-Fit Inc Classes</span>
          </Link>
          <div className="flex gap-4">
            {user ? (
              <>
                <Link to="/classes">
                  <Button variant="ghost" data-testid="nav-classes-btn">Classes</Button>
                </Link>
                <Link to="/dashboard">
                  <Button data-testid="nav-dashboard-btn">Dashboard</Button>
                </Link>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" data-testid="nav-login-btn">Login</Button>
                </Link>
                <Link to="/classes">
                  <Button data-testid="nav-browse-btn">Browse Classes</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        <div className="hero-glow absolute inset-0 pointer-events-none" />
        
        <div className="max-w-7xl mx-auto px-6 py-24 grid md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tight mb-6">
              ELEVATE YOUR
              <span className="block text-primary">FITNESS JOURNEY</span>
            </h1>
            <p className="text-lg text-neutral-300 mb-8 max-w-xl">
              Book premium fitness classes with top instructors. Pay securely, train confidently, achieve relentlessly.
            </p>
            <div className="flex gap-4">
              <Link to="/classes">
                <Button size="lg" className="bg-primary hover:bg-primary-hover text-white font-bold" data-testid="hero-book-now-btn">
                  BOOK NOW
                </Button>
              </Link>
              {!user && (
                <Link to="/register">
                  <Button size="lg" variant="outline" className="border-white/20" data-testid="hero-get-started-btn">
                    GET STARTED
                  </Button>
                </Link>
              )}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="relative rounded-sm overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1758875569517-559a631fb44d"
                alt="High intensity battle ropes training"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent" />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-background-paper">
        <div className="max-w-7xl mx-auto px-6">
          <motion.h2
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="font-heading text-4xl sm:text-5xl font-black text-center mb-16"
          >
            WHY CHOOSE US
          </motion.h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-black/40 backdrop-blur-md border border-white/10 p-8 rounded-sm hover:border-primary/50 transition-colors duration-300"
                data-testid={`feature-card-${index}`}
              >
                <div className="text-primary mb-4">{feature.icon}</div>
                <h3 className="font-heading text-xl font-bold mb-2">{feature.title}</h3>
                <p className="text-neutral-400 text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl sm:text-5xl font-black mb-6">
              READY TO TRANSFORM?
            </h2>
            <p className="text-lg text-neutral-300 mb-8">
              Join thousands of fitness enthusiasts who trust T-Fit Inc Classes for their training needs.
            </p>
            <Link to="/classes">
              <Button size="lg" className="bg-primary hover:bg-primary-hover text-white font-bold" data-testid="cta-explore-btn">
                EXPLORE CLASSES
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center text-neutral-500 text-sm space-y-2">
          <div className="flex items-center justify-center gap-4">
            <Link to="/privacy-policy" className="hover:text-white transition-colors">Privacy Policy</Link>
            <span>•</span>
            <a href="mailto:admin@tfitinc.co.uk" className="hover:text-white transition-colors">Contact</a>
          </div>
          <p>© 2025 T-Fit Inc Classes. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;